if (!customElements.get('bundle-comparison')) {
  customElements.define(
    'bundle-comparison',
    class BundleComparison extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.cards = Array.from(this.querySelectorAll('.ttbc-card'));
        this.products = this.cards.map((card) => {
          let data = {};
          try { data = JSON.parse(card.querySelector('.ttbc-card__data').textContent); } catch (error) { console.warn('Bundle comparison data could not be read.', error); }
          data.id = String(data.id || card.dataset.productId || '');
          data.variantId = String(data.variantId || '');
          data.available = data.available !== false && Boolean(data.variantId);
          data.contents = this.toList(data.contents);
          data.callouts = this.toList(data.callouts);
          data.contentsText = data.contents.join(' ').toLowerCase();
          data.searchText = this.flatten([data.title, data.audience, data.ages, data.contents, data.callouts]).toLowerCase();
          data.card = card;
          return data;
        });
        this.productMap = new Map(this.products.map((product) => [product.id, product]));
        this.differencesOnly = false;
        this.activeFilter = this.querySelector('[data-ttbc-filter="special"]') ? 'special' : 'all';
        this.comparisonInView = false;
        this.comparisonPanels = Array.from(this.querySelectorAll('[data-ttbc-comparison-panel]'));
        this.comparisonPanel = this.comparisonPanels[0] || null;
        this.visibleComparisonPanels = new Set();
        this.compareTray = this.querySelector('[data-ttbc-compare-tray]');
        this.compareTrayThumbs = this.querySelector('[data-ttbc-tray-thumbs]');
        this.compareTrayTitle = this.querySelector('[data-ttbc-tray-title]');
        this.compareTrayMessage = this.querySelector('[data-ttbc-tray-message]');
        this.viewComparisonButton = this.querySelector('[data-ttbc-view-comparison]');
        this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add';
        this.addToCartLabel = this.dataset.addToCartLabel || 'Add to cart';
        this.mobileQuery = window.matchMedia('(max-width: 749px)');

        let defaults = [];
        this.querySelectorAll('.ttbc-defaults').forEach((script) => {
          if (defaults.length) return;
          try {
            const configuredDefaults = JSON.parse(script.textContent || '[]').map(String);
            if (configuredDefaults.length) defaults = configuredDefaults;
          } catch (error) {
            defaults = [];
          }
        });
        this.selected = defaults.filter((id) => this.productMap.has(id)).slice(0, this.selectionLimit());
        this.mobilePair = this.selected.slice(0, 2);

        this.handleBreakpointChange = () => {
          const limit = this.selectionLimit();
          if (this.selected.length > limit) this.selected = this.selected.slice(0, limit);
          this.mobilePair = this.selected.slice(0, 2);
          this.syncCompareButtons();
          this.renderComparison();
        };
        if (this.mobileQuery.addEventListener) {
          this.mobileQuery.addEventListener('change', this.handleBreakpointChange);
        } else {
          this.mobileQuery.addListener(this.handleBreakpointChange);
        }

        this.addEventListener('click', (event) => this.handleClick(event));
        this.addEventListener('change', (event) => this.handleChange(event));
        this.cardTrack = this.querySelector('.ttbc__cards');
        this.cardTrack?.addEventListener('scroll', () => this.updateScrollArrows(), { passive: true });
        this.resizeObserver = new ResizeObserver(() => this.updateScrollArrows());
        if (this.cardTrack) this.resizeObserver.observe(this.cardTrack);
        if ('IntersectionObserver' in window && this.comparisonPanels.length) {
          this.comparisonObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.visibleComparisonPanels.add(entry.target);
              else this.visibleComparisonPanels.delete(entry.target);
            });
            this.comparisonInView = this.visibleComparisonPanels.size > 0;
            this.updateCompareTray();
          }, { threshold: .08 });
          this.comparisonPanels.forEach((panel) => this.comparisonObserver.observe(panel));
        }
        this.applyCardVisibility();
        this.syncCompareButtons();
        this.renderComparison();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.comparisonObserver?.disconnect();
        if (this.mobileQuery?.removeEventListener) {
          this.mobileQuery.removeEventListener('change', this.handleBreakpointChange);
        } else {
          this.mobileQuery?.removeListener(this.handleBreakpointChange);
        }
      }

      selectionLimit() {
        return this.mobileQuery?.matches ? 2 : 3;
      }

      toList(value) {
        if (Array.isArray(value)) return value.map((item) => this.flatten(item)).filter(Boolean);
        const flattened = this.flatten(value);
        return flattened ? [flattened] : [];
      }

      flatten(value) {
        if (value === null || typeof value === 'undefined') return '';
        if (typeof value === 'string' || typeof value === 'number') return String(value);
        if (Array.isArray(value)) return value.map((item) => this.flatten(item)).filter(Boolean).join(' ');
        if (typeof value === 'object') {
          const preferred = ['label', 'name', 'value', 'display_name'];
          const preferredValues = preferred.filter((key) => key in value).map((key) => this.flatten(value[key])).filter(Boolean);
          if (preferredValues.length) return preferredValues.join(' ');
          return Object.values(value).map((item) => this.flatten(item)).filter(Boolean).join(' ');
        }
        return '';
      }

      escape(value) {
        const div = document.createElement('div');
        div.textContent = value == null ? '' : String(value);
        return div.innerHTML;
      }

      handleClick(event) {
        const scroll = event.target.closest('[data-ttbc-scroll]');
        if (scroll && this.cardTrack) {
          const direction = scroll.dataset.ttbcScroll === 'next' ? 1 : -1;
          this.cardTrack.scrollBy({ left: this.cardTrack.clientWidth * 0.82 * direction, behavior: 'smooth' });
          return;
        }

        const filter = event.target.closest('[data-ttbc-filter]');
        if (filter) {
          this.activeFilter = filter.dataset.ttbcFilter;
          this.querySelectorAll('[data-ttbc-filter]').forEach((button) => button.setAttribute('aria-pressed', String(button === filter)));
          this.applyCardVisibility();
          return;
        }

        const compare = event.target.closest('[data-ttbc-compare]');
        if (compare) {
          const id = compare.dataset.ttbcCompare;
          const limit = this.selectionLimit();
          if (this.selected.includes(id)) {
            this.selected = this.selected.filter((selectedId) => selectedId !== id);
          } else if (this.selected.length < limit) {
            this.selected.push(id);
          }
          this.mobilePair = this.selected.slice(0, 2);
          this.syncCompareButtons();
          this.renderComparison();
          return;
        }

        const viewComparison = event.target.closest('[data-ttbc-view-comparison]');
        if (viewComparison) {
          const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
          this.comparisonPanel?.scrollIntoView({ behavior, block: 'start' });
          return;
        }

        const differences = event.target.closest('[data-ttbc-differences]');
        if (differences) {
          this.differencesOnly = !this.differencesOnly;
          this.querySelectorAll('[data-ttbc-differences]').forEach((button) => {
            button.setAttribute('aria-pressed', String(this.differencesOnly));
            button.textContent = this.differencesOnly ? button.dataset.onLabel : button.dataset.offLabel;
          });
          this.renderComparison();
        }
      }

      handleChange(event) {
        const select = event.target.closest('[data-ttbc-mobile-select]');
        if (!select) return;
        const index = Number(select.dataset.ttbcMobileSelect);
        this.mobilePair[index] = select.value;
        if (this.mobilePair[0] === this.mobilePair[1] && this.products.length > 1) {
          const replacement = this.products.find((product) => product.id !== select.value);
          this.mobilePair[index === 0 ? 1 : 0] = replacement.id;
        }
        this.renderComparison();
      }

      matches(product, filter) {
        const contents = product.contentsText;
        if (filter === 'toddlers') return /\b6\s+(?:children['’]?s\s+)?board\s*books?\b/i.test(contents);
        if (filter === 'kids') return /\b14\s+children['’]?s books?\b|america['’]?s history/i.test(contents);
        if (filter === 'teens') return /\b7\s+guide\s*books?\b|\b5\s+choose your consequence adventure books?\b/i.test(contents);
        if (filter === 'history') return /america['’]?s history/i.test(contents);
        if (filter === 'digital') return /\b14\s+activity workbooks?\s*\(pdf\)|\b14\s+parent guides?\s*\(pdf\)|\b14\s+audiobooks?\b|america['’]?s history(?:\s*:\s*|\s+)curriculum|\baudiobooks?\b|\bvideos?\b/i.test(contents);
        if (filter === 'complete') return /\bmega bundle\b/i.test(product.title || '');
        if (filter === 'new') return product.new === true || this.toList(product.callouts).some((callout) => callout.trim().toLowerCase() === 'new');
        if (filter === 'special') return product.special === true;
        return true;
      }

      applyCardVisibility() {
        let matchCount = 0;
        this.products.forEach((product) => {
          const match = this.matches(product, this.activeFilter);
          if (match) matchCount += 1;
          product.card.hidden = false;
          product.card.classList.toggle('ttbc-card--match', this.activeFilter !== 'all' && match);
          product.card.classList.toggle('ttbc-card--soft', this.activeFilter !== 'all' && !match);
          product.card.style.order = this.activeFilter !== 'all' && match ? '-1' : '';
          let cardOrder = '';
          if (this.activeFilter !== 'all' && match) {
            const isToddlerCombo = this.activeFilter === 'toddlers' && /toddler(?:s)?\s+combo\s+set/i.test(product.title || '');
            const isTeenMegaBundle = this.activeFilter === 'teens' && /\bmega bundle\b/i.test(product.title || '');
            const isHistoryVolumes = this.activeFilter === 'history' && /america['’]?s history.*\bvol(?:ume)?s?\.?\s*-?\s*1\s*(?:-|–|—|to)\s*3\b/i.test(product.title || '');
            const isHistoryMegaBundle = this.activeFilter === 'history' && /\bmega bundle\b/i.test(product.title || '');
            if (isToddlerCombo) {
              cardOrder = '-2';
            } else if (this.activeFilter === 'teens') {
              cardOrder = isTeenMegaBundle ? '-1' : '-2';
            } else if (this.activeFilter === 'history') {
              cardOrder = isHistoryVolumes ? '-3' : isHistoryMegaBundle ? '-1' : '-2';
            } else {
              cardOrder = '-1';
            }
          }
          product.card.style.order = cardOrder;
        });

        const status = this.querySelector('[data-ttbc-status]');
        if (status) {
          status.textContent = this.activeFilter === 'all' ? '' : `${matchCount} best ${matchCount === 1 ? 'match' : 'matches'} moved to the front. All bundles are still shown.`;
        }
        if (this.cardTrack) this.cardTrack.scrollTo({ left: 0, behavior: 'smooth' });
        requestAnimationFrame(() => this.updateScrollArrows());
      }

      updateScrollArrows() {
        if (!this.cardTrack) return;
        const previous = this.querySelector('[data-ttbc-scroll="prev"]');
        const next = this.querySelector('[data-ttbc-scroll="next"]');
        if (previous) previous.disabled = this.cardTrack.scrollLeft < 8;
        if (next) next.disabled = this.cardTrack.scrollLeft + this.cardTrack.clientWidth >= this.cardTrack.scrollWidth - 8;
      }

      syncCompareButtons() {
        const limit = this.selectionLimit();
        this.querySelectorAll('[data-ttbc-compare]').forEach((button) => {
          const selected = this.selected.includes(button.dataset.ttbcCompare);
          button.closest('.ttbc-card')?.classList.toggle('ttbc-card--selected', selected);
          button.closest('.ttbc-card')?.classList.remove('ttbc-card--selected');
          button.setAttribute('aria-pressed', String(selected));
          button.textContent = selected ? button.dataset.selectedLabel : button.dataset.defaultLabel;
          button.disabled = !selected && this.selected.length >= limit;
        });
        this.querySelectorAll('[data-ttbc-compare-hint]').forEach((hint) => {
          hint.textContent = `${this.selected.length} of ${limit} selected`;
        });
        this.comparisonPanels.forEach((panel) => { panel.hidden = this.selected.length === 0; });
        if (this.selected.length === 0) {
          this.visibleComparisonPanels.clear();
          this.comparisonInView = false;
        }
        this.querySelectorAll('[data-ttbc-differences]').forEach((differences) => {
          differences.disabled = this.selected.length < 2;
        });
      }

      parseContent(value) {
        const separator = value.indexOf(':');
        if (separator === -1) return { group: 'Bundle extras', item: value.trim(), raw: value };
        return { group: value.slice(0, separator).trim(), item: value.slice(separator + 1).trim(), raw: value };
      }

      rowsFor(products) {
        const groups = new Map();
        products.forEach((product) => {
          product.contents.forEach((content) => {
            const parsed = this.parseContent(content);
            if (!groups.has(parsed.group)) groups.set(parsed.group, new Map());
            if (!groups.get(parsed.group).has(parsed.raw)) groups.get(parsed.group).set(parsed.raw, parsed);
          });
        });
        return groups;
      }

      isDifferent(values) {
        return new Set(values.map((value) => String(value))).size > 1;
      }

      renderComparison() {
        const desktopProducts = this.selected.map((id) => this.productMap.get(id)).filter(Boolean);
        const mobileProducts = this.mobilePair.map((id) => this.productMap.get(id)).filter(Boolean);
        this.renderDesktop(desktopProducts);
        this.renderMobile(mobileProducts);
        this.updateCompareTray();
      }

      updateCompareTray() {
        if (!this.compareTray) return;
        const count = this.selected.length;
        const limit = this.selectionLimit();
        const selectedProducts = this.selected.map((id) => this.productMap.get(id)).filter(Boolean);
        const shouldShow = count > 0 && (count === 1 || !this.comparisonInView);
        this.compareTray.hidden = !shouldShow;

        if (count === 0) {
          if (this.compareTrayThumbs) this.compareTrayThumbs.innerHTML = '';
          return;
        }

        if (this.compareTrayThumbs) {
          this.compareTrayThumbs.innerHTML = selectedProducts.map((product) => {
            if (product.image) {
              return `<img class="ttbc-compare-tray__thumb" src="${this.escape(product.image)}" alt="">`;
            }
            const initial = this.escape((product.title || '?').trim().charAt(0));
            return `<span class="ttbc-compare-tray__thumb ttbc-compare-tray__thumb--placeholder">${initial}</span>`;
          }).join('');
        }

        if (this.compareTrayTitle) {
          this.compareTrayTitle.textContent = `${count} ${count === 1 ? 'bundle' : 'bundles'} selected`;
        }

        if (this.compareTrayMessage) {
          this.compareTrayMessage.textContent = count === 1
            ? 'Choose one more to compare.'
            : count === 2
              ? 'Your comparison is ready.'
              : `Maximum of ${limit} selected.`;
        }

        if (this.viewComparisonButton) {
          this.viewComparisonButton.hidden = count < 2;
          this.viewComparisonButton.textContent = count === limit ? 'Compare now ↓' : 'View comparison ↓';
        }
      }

      productHead(product) {
        const image = product.image ? `<img class="ttbc-table__image" src="${this.escape(product.image)}" alt="" loading="lazy">` : '';
        return `<div class="ttbc-table__product">${image}<a class="ttbc-table__title" href="${this.escape(product.url)}">${this.escape(product.title)}</a></div>`;
      }

      cartForm(product, context) {
        const available = product.available && product.variantId;
        const label = available ? this.addToCartLabel : 'Sold out';
        const formId = `ttbc-compare-cart-${context}-${this.escape(product.id)}`;
        return `
          <product-form class="ttbc-table__product-form" data-hide-errors="false">
            <form method="post" action="${this.escape(this.cartAddUrl)}" id="${formId}" class="form" enctype="multipart/form-data" data-type="add-to-cart">
              <input type="hidden" name="id" value="${this.escape(product.variantId)}" class="product-variant-id">
              <button
                type="submit"
                name="add"
                class="ttbc-table__cart-button button button--full-width button--primary"
                aria-label="${this.escape(label)} — ${this.escape(product.title)}"
                ${available ? '' : 'disabled'}
              >
                <span>${this.escape(label)}</span>
                <span class="sold-out-message hidden">Sold out</span>
                <span class="loading__spinner hidden" aria-hidden="true"></span>
              </button>
              <div class="product-form__error-message-wrapper" role="alert" hidden>
                <span class="product-form__error-message"></span>
              </div>
            </form>
          </product-form>`;
      }

      ageText(product) {
        return this.flatten(product.ages) || '—';
      }

      renderDesktop(products) {
        const targets = this.querySelectorAll('[data-ttbc-desktop-table]');
        targets.forEach((target, chartIndex) => {
          if (products.length < 2) {
            target.innerHTML = '<p style="padding:2rem;text-align:center">Select at least two bundles to compare.</p>';
            return;
          }
          let html = '<table class="ttbc-table"><thead><tr><th scope="col"></th>';
          products.forEach((product) => { html += `<th scope="col">${this.productHead(product)}</th>`; });
          html += '</tr></thead><tbody>';
          const ages = products.map((product) => this.ageText(product));
          const prices = products.map((product) => this.flatten(product.price) || '—');
          let overviewHtml = '';
          if (!this.differencesOnly || this.isDifferent(ages)) overviewHtml += `<tr><th scope="row">Recommended ages</th>${ages.map((age) => `<td>${this.escape(age)}</td>`).join('')}</tr>`;
          if (!this.differencesOnly || this.isDifferent(prices)) overviewHtml += `<tr><th scope="row">Price</th>${prices.map((price) => `<td><span class="ttbc-table__price">${this.escape(price)}</span></td>`).join('')}</tr>`;
          if (overviewHtml) html += `<tr class="ttbc-table__group"><th colspan="${products.length + 1}">At a Glance</th></tr>${overviewHtml}`;
          this.rowsFor(products).forEach((rows, group) => {
            let groupHtml = '';
            rows.forEach((row) => {
              const values = products.map((product) => product.contents.includes(row.raw));
              if (this.differencesOnly && !this.isDifferent(values)) return;
              groupHtml += `<tr><th scope="row">${this.escape(row.item)}</th>${values.map((included) => `<td><span class="${included ? 'ttbc-table__check' : 'ttbc-table__dash'}" aria-label="${included ? 'Included' : 'Not included'}">${included ? '✓' : '—'}</span></td>`).join('')}</tr>`;
            });
            if (groupHtml) html += `<tr class="ttbc-table__group"><th colspan="${products.length + 1}">${this.escape(group)}</th></tr>${groupHtml}`;
          });
          html += `<tr class="ttbc-table__cart-row"><th scope="row">Choose your bundle</th>${products.map((product) => `<td>${this.cartForm(product, 'desktop-' + chartIndex)}</td>`).join('')}</tr>`;
          html += '</tbody></table>';
          target.innerHTML = html;
        });
      }

      updatedMobileHtml(products, chartIndex) {
        let html = '';
        const ages = products.map((product) => this.ageText(product));
        const prices = products.map((product) => this.flatten(product.price) || '—');

        if (!this.differencesOnly || this.isDifferent(ages)) {
          html += `<div class="ttbc-mobile-row--group">Recommended Ages</div>`;
          html += `<div class="ttbc-mobile-row ttbc-mobile-row--updated-values"><span>${this.escape(ages[0])}</span><span>${this.escape(ages[1])}</span></div>`;
        }

        if (!this.differencesOnly || this.isDifferent(prices)) {
          html += `<div class="ttbc-mobile-row--group">Price</div>`;
          html += `<div class="ttbc-mobile-row ttbc-mobile-row--updated-values"><span class="ttbc-table__price">${this.escape(prices[0])}</span><span class="ttbc-table__price">${this.escape(prices[1])}</span></div>`;
        }

        this.rowsFor(products).forEach((rows) => {
          rows.forEach((row) => {
            const values = products.map((product) => product.contents.includes(row.raw));
            if (this.differencesOnly && !this.isDifferent(values)) return;
            const hasSeriesContext = row.group && row.group.toLowerCase() !== 'bundle extras';
            const featureLabel = hasSeriesContext
              ? `<span class="ttbc-mobile-feature__context">${this.escape(row.group)}</span><span class="ttbc-mobile-feature__item">${this.escape(row.item)}</span>`
              : `<span class="ttbc-mobile-feature__item">${this.escape(row.item)}</span>`;
            html += `<div class="ttbc-mobile-row--group ttbc-mobile-row--feature-label">${featureLabel}</div>`;
            html += `<div class="ttbc-mobile-row ttbc-mobile-row--updated-values">${values.map((included) => `<span class="${included ? 'ttbc-table__check' : 'ttbc-table__dash'}" aria-label="${included ? 'Included' : 'Not included'}">${included ? '✓' : '—'}</span>`).join('')}</div>`;
          });
        });

        html += `<div class="ttbc-mobile-row ttbc-mobile-row--cart"><div>${this.cartForm(products[0], 'mobile-' + chartIndex + '-a')}</div><div>${this.cartForm(products[1], 'mobile-' + chartIndex + '-b')}</div></div>`;
        return html;
      }

      renderMobile(products) {
        const selectors = this.querySelectorAll('[data-ttbc-mobile-select]');
        selectors.forEach((select) => {
          const index = Number(select.dataset.ttbcMobileSelect);
          const placeholder = `<option value="" ${this.mobilePair[index] ? '' : 'selected'} disabled>Select a bundle</option>`;
          select.innerHTML = placeholder + this.products.map((product) => `<option value="${this.escape(product.id)}" ${this.mobilePair[index] === product.id ? 'selected' : ''}>${this.escape(product.title)}</option>`).join('');
        });
        this.querySelectorAll('[data-ttbc-mobile-table]').forEach((target, chartIndex) => {
          if (products.length < 2) {
            target.innerHTML = '<p style="padding:2rem;text-align:center">Select at least two bundles to compare.</p>';
            return;
          }
          if (target.closest('[data-ttbc-comparison-version="updated"]')) {
            target.innerHTML = this.updatedMobileHtml(products, chartIndex);
            return;
          }
          let html = `<div class="ttbc-mobile-row ttbc-mobile-row--products"><span></span><span>${this.escape(products[0].title)}</span><span>${this.escape(products[1].title)}</span></div>`;
          const ages = products.map((product) => this.ageText(product));
          const prices = products.map((product) => this.flatten(product.price) || '—');
          let overviewHtml = '';
          if (!this.differencesOnly || this.isDifferent(ages)) overviewHtml += `<div class="ttbc-mobile-row"><strong>Recommended Ages</strong><span>${this.escape(ages[0])}</span><span>${this.escape(ages[1])}</span></div>`;
          if (!this.differencesOnly || this.isDifferent(prices)) overviewHtml += `<div class="ttbc-mobile-row"><strong>Price</strong><span class="ttbc-table__price">${this.escape(prices[0])}</span><span class="ttbc-table__price">${this.escape(prices[1])}</span></div>`;
          if (overviewHtml) html += `<div class="ttbc-mobile-row--group">At a Glance</div>${overviewHtml}`;
          this.rowsFor(products).forEach((rows, group) => {
            let groupHtml = '';
            rows.forEach((row) => {
              const values = products.map((product) => product.contents.includes(row.raw));
              if (this.differencesOnly && !this.isDifferent(values)) return;
              groupHtml += `<div class="ttbc-mobile-row"><span>${this.escape(row.item)}</span>${values.map((included) => `<span class="${included ? 'ttbc-table__check' : 'ttbc-table__dash'}" aria-label="${included ? 'Included' : 'Not included'}">${included ? '✓' : '—'}</span>`).join('')}</div>`;
            });
            if (groupHtml) html += `<div class="ttbc-mobile-row--group">${this.escape(group)}</div>${groupHtml}`;
          });
          html += `<div class="ttbc-mobile-row ttbc-mobile-row--cart"><div>${this.cartForm(products[0], 'mobile-' + chartIndex + '-a')}</div><div>${this.cartForm(products[1], 'mobile-' + chartIndex + '-b')}</div></div>`;
          target.innerHTML = html;
        });
      }
    }
  );
}
